/**
 * Utility functions for credit date validation
 */

/**
 * Creates a date object with only the date part (ignoring time)
 * @param date - The date to convert
 * @returns Date object with time set to midnight
 */
export const createDateOnly = (date: Date | string): Date => {
  const dateObj = new Date(date);
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
};

/**
 * Validates if a selected date is valid for credit extension
 * @param selectedDate - The date selected by user
 * @param originalExpiryDate - The original expiry date of the credit
 * @param maxDate - The maximum allowed date for extension
 * @returns Object with validation results
 */
export const validateCreditExtensionDate = (
  selectedDate: string,
  originalExpiryDate: string,
  maxDate: Date | null
) => {
  const selectedDateOnly = createDateOnly(selectedDate);
  const originalExpiryDateOnly = createDateOnly(originalExpiryDate);

  // Allow dates within 1 day before the original expiry (for timezone issues)
  const oneDayInMs = 24 * 60 * 60 * 1000;
  const isDateBeforeOriginalExpiry =
    selectedDateOnly.getTime() < originalExpiryDateOnly.getTime() - oneDayInMs;

  const isDateExceedsMax =
    maxDate && selectedDateOnly.getTime() > maxDate.getTime();

  return {
    isDateBeforeOriginalExpiry,
    isDateExceedsMax,
    isValid: !isDateBeforeOriginalExpiry && !isDateExceedsMax
  };
};

/**
 * Creates the original expiry date object from expiry date string
 * @param expiryDate - The expiry date string
 * @returns Date object representing the original expiry date
 */
export const getOriginalExpiryDate = (expiryDate: string): Date => {
  return new Date(expiryDate);
};
